import React from "react";
import { Link } from "react-router-dom";
import { Building, FinancialSummary } from "@akfdev-com/shared/src/models";
import { AddressForm, PartialAddress } from "../ui";

interface BuildingOverviewProps {
  building: Building | null;
  financialSummary: FinancialSummary | null;
  isEditing: boolean;
  editForm: Partial<Building>;
  handleInputChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  /**
   * Structured-address handler (address-geolocation Rule 21). See
   * BuildingSettings.tsx for the same contract.
   */
  onStructuredAddressChange?: (next: PartialAddress) => void;
}

const BuildingOverview: React.FC<BuildingOverviewProps> = ({
  building,
  financialSummary,
  isEditing,
  editForm,
  handleInputChange,
  onStructuredAddressChange,
}) => {
  // FinancialSummary values are returned by the API in pence (consistent with
  // the rest of the system, e.g. FinancialRecord.amount). Convert to pounds here.
  const formatCurrency = (amountInPence: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format((amountInPence ?? 0) / 100);
  };

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Building Information Section */}
        <div className="lg:col-span-2">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Building Information
          </h3>
          <div className="space-y-4">
            {isEditing ? (
              <>
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Building Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={editForm.name || ""}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                {/* Structured Address form (address-geolocation Rule 21).
                    The legacy free-text `Building.address` string is
                    retained on the record and re-derived via formatAddress
                    on save (BuildingDetails.tsx). */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  {editForm.address && !building?.structuredAddress ? (
                    <p className="text-xs text-gray-500 mb-2 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                      Auto-parsed from legacy address:{" "}
                      <span className="font-medium">{editForm.address}</span>
                      <br />
                      Review the fields below and correct anything that
                      mis-mapped. The legacy string will be regenerated on save.
                    </p>
                  ) : null}
                  <AddressForm
                    value={editForm.structuredAddress ?? {}}
                    onChange={onStructuredAddressChange ?? (() => undefined)}
                    idPrefix="building-overview"
                    disabled={!onStructuredAddressChange}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="unitsCount"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Number of units{" "}
                      {!building && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="number"
                      name="unitsCount"
                      id="unitsCount"
                      min={0}
                      max={500}
                      value={editForm.unitsCount ?? ""}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                    {!building && (
                      <p className="mt-1 text-xs text-gray-500">
                        On save, N outline units will be created named{" "}
                        <code>Unit #1</code> …{" "}
                        <code>Unit #{editForm.unitsCount || "N"}</code> with
                        equal shares of{" "}
                        <code>1/{editForm.unitsCount || "N"}</code>. Rename them
                        or adjust shares from the Units tab afterwards.
                      </p>
                    )}
                  </div>
                </div>

                {/* Building schedule (Bucket B #5).
                    - builtDate: original construction date. Optional.
                    - managementStartDate: when the current management
                      company started managing this building. Drives the
                      default `moveInDate` on resident onboarding. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="builtDate"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Built Date
                    </label>
                    <input
                      type="date"
                      name="builtDate"
                      id="builtDate"
                      value={editForm.builtDate || ""}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="managementStartDate"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Management Start Date
                    </label>
                    <input
                      type="date"
                      name="managementStartDate"
                      id="managementStartDate"
                      value={editForm.managementStartDate || ""}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Anchor date for resident onboarding + accrual.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Units</h4>
                    <p className="mt-1 text-sm text-gray-900">
                      {building ? building.unitsCount : "-"}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500">
                      Last Updated
                    </h4>
                    <p className="mt-1 text-sm text-gray-900">
                      {building && building.updatedAt
                        ? new Date(building.updatedAt).toLocaleDateString()
                        : "-"}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Financial Summary Section */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Financial Summary
          </h3>
          {financialSummary ? (
            <>
              <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500">
                      Current Balance
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(financialSummary.currentBalance)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-gray-500">
                      Reserve Fund
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(financialSummary.reserveFund)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-gray-500">
                      Total Income
                    </p>
                    <p className="text-sm font-medium text-green-600">
                      {formatCurrency(financialSummary.totalIncome)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-gray-500">
                      Total Bills
                    </p>
                    <p className="text-sm font-medium text-red-600">
                      {formatCurrency(financialSummary.totalBills)}
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-500">
                    Budget Compliance
                  </p>
                  <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full"
                      style={{ width: `${financialSummary.budgetCompliance}%` }}
                    ></div>
                  </div>
                  <p className="mt-1 text-xs text-gray-600 text-right">
                    {financialSummary.budgetCompliance}%
                  </p>
                </div>

                {financialSummary.outstandingPayments > 0 && (
                  <div className="mt-4 p-2 bg-yellow-50 rounded-md">
                    <p className="text-xs font-medium text-yellow-800">
                      Outstanding Payments
                    </p>
                    <p className="text-sm font-medium text-yellow-800">
                      {formatCurrency(financialSummary.outstandingPayments)}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <Link
                  to={
                    building
                      ? `/dashboard/buildings/${building.id}/financials`
                      : "#"
                  }
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                  aria-disabled={!building}
                  tabIndex={!building ? -1 : 0}
                >
                  View detailed financial reports →
                </Link>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              Financial data is not available.
            </p>
          )}
        </div>
      </div>

      {/* Quick Actions Section */}
      <div className="mt-8 border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Quick Actions
        </h3>
        {/* Quick Actions row — buildings Rule 17. Exactly three actions in
            this order: Add Expense (primary) · Generate Statements · Manage
            Units. Create Invoice was removed in v20 (Rule 17) — ad-hoc
            invoicing belongs on the Financial Records page. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to={`/dashboard/financial-records/new?type=bill&building=${
              building ? building.id : ""
            }`}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
            aria-disabled={!building}
            tabIndex={!building ? -1 : 0}
          >
            Add Expense
          </Link>

          <Link
            to={`/dashboard/statements?building=${building ? building.id : ""}`}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            aria-disabled={!building}
            tabIndex={!building ? -1 : 0}
          >
            Generate Statements
          </Link>

          <Link
            to={`/dashboard/units?building=${building ? building.id : ""}`}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            aria-disabled={!building}
            tabIndex={!building ? -1 : 0}
          >
            Manage Units
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BuildingOverview;
